/**
 * Component for truncating a paragraph's text and appending "...show more" link
 * Clicking the "...show more" link will expand the paragraph to show full text
 * "...show more" link shows conditionally according to props
 *
 * @version 1.0.0
 **/
import React, {useState} from 'react';
import styled from 'styled-components';
import {Typography} from '../typography';
import root from 'window-or-global';

export interface TruncateTextProps {
    text?: string;
    className?: string;
    /* props that determine if text will be truncated */
    enableTruncation?: true | false;
    viewport?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '';
    viewportType?: 'up' | 'down' | 'only' | '';
    maxViewportHeight?: number;
    /* max text lines to show when text is truncated */
    maxLines?: number | string;
    typographyVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p';
    hideShowMoreButton?: boolean;
}

const TruncateTextParagraphWrap = styled.div<{
    viewport: string;
    viewportType: string;
    maxViewportHeight: number;
    maxLines: number | string;
}>`
    .showMore .paragraph {
        display: none;
    }
    ${(p) => p.theme.breakpoints[p.viewportType](p.viewport)} {
        .truncated .truncatedText {
            -webkit-line-clamp: ${(p) =>
                root.innerHeight >= p.maxViewportHeight ? p.maxLines : 'initial'};
            display: -webkit-box;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .truncated .showMore .paragraph {
            display: ${(p) => (root.innerHeight >= p.maxViewportHeight ? 'block' : 'none')};
            text-decoration: underline;
        }
    }
`;

export const TruncateTextParagraph = (props: TruncateTextProps): JSX.Element => {
    const {
        text,
        enableTruncation = true,
        className,
        viewport = 'xl',
        viewportType = 'down',
        maxViewportHeight = 0,
        maxLines = 'initial',
        typographyVariant = 'p',
        hideShowMoreButton
    } = props;

    const [truncate, setEnableTruncate] = useState(true);

    return (
        <TruncateTextParagraphWrap
            className={className}
            viewport={viewport}
            viewportType={viewportType}
            maxViewportHeight={maxViewportHeight}
            maxLines={maxLines}>
            <div className={truncate && enableTruncation ? 'truncated' : ''}>
                <Typography className="truncatedText" variant={typographyVariant}>
                    {text}
                </Typography>
                {!hideShowMoreButton && (
                    <div
                        className="showMore"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => setEnableTruncate(false)}>
                        <Typography variant="p">Show More</Typography>
                    </div>
                )}
            </div>
        </TruncateTextParagraphWrap>
    );
};
